import { IAuditChange, IAuditEvent } from '../hooks/reactQuerys/AuditQueries';

/** Satzbildung für das Änderungsprotokoll (#1330).
 *
 * Die Texte entstehen im Frontend, nicht im Backend. Gespeichert werden
 * `event_type` und strukturierte `Changes` — dadurch ändern Formulierungs-
 * anpassungen keine historischen Datensätze, und Filter bleiben möglich.
 */

/** Anzeigename einer Herkunft. */
const SOURCE_LABEL: Record<string, string> = {
  ui: 'DiNo',
  generic_api: 'Schnittstelle',
  portal_sync: 'Mandantenportal',
  dialog_inbox: 'Dialogeingang',
  dino_import: 'DiNo-Import',
  arnotop_sync: 'ARNOtop',
  process_manager: 'Automatik',
  scheduler: 'Nachtlauf',
  cron: 'Systemaufruf',
  hbci: 'Notaranderkonto',
  land_register: 'Grundbuchportal',
  trade_register: 'Handelsregister',
  tablet: 'Tablet',
  plugin: 'Plugin',
  license_server: 'Lizenzserver',
  seed_import: 'Datenimport',
  migration: 'Migration',
  healthcheck: 'Datenpflege',
  unknown: 'unbekannt',
};

/** Kurzform für den Akteurstyp — die Frage "Mensch oder Automatik?" ist
 *  wichtiger als der Name. */
const ACTOR_LABEL: Record<string, string> = {
  user: 'Mitarbeiter',
  client: 'Mandant',
  device: 'Gerät',
  system: 'System',
  integration: 'Fremdsystem',
};

/** Satzschablonen für die fachlichen Ereignisse. Fehlt eine, greift der
 *  Fallback weiter unten — es bleibt also nie eine Zeile leer. */
/**
 * Arten der Nachverfolgung. Dieselben Typen, die in der Datenbank `isVisible`
 * tragen — alles andere im ProcessManager ist Sync- und Job-Verwaltung und
 * erzeugt gar kein Ereignis.
 */
const TRACKING_TYPE_LABEL: Record<number, string> = {
  10: 'Grundbuch-Aktualitätsnachweis',
  11: 'Grundbuch-Markentabelle',
  20: 'Handelsregister',
  21: 'Gesellschafterliste',
  22: 'Notaranderkonto-Saldo',
};

const TRACKING_STATE_LABEL: Record<number, string> = {
  1: 'offen',
  2: 'übermittelt',
  3: 'eingetragen',
};

const EVENT_TEXT: Record<string, string> = {
  'legal_transaction.created': 'Vorgang angelegt',
  'legal_transaction.imported_from_dialog':
    'Vorgang aus Dialogeingang importiert',
  'legal_transaction.status_changed': 'Status geändert',
  'legal_transaction.type_changed': 'Rechtsgeschäftsart geändert',
  'legal_transaction.type_migrated': 'Vollzugsstand migriert',
  'legal_transaction.closed': 'Vorgang abgeschlossen',
  'legal_transaction.reopened': 'Vorgang reaktiviert',
  'legal_transaction.deleted': 'Vorgang gelöscht',
  'legal_transaction.saved': 'Vorgang gespeichert',
  'legal_transaction.reactivated': 'Vorgang reaktiviert',
  'legal_transaction.invite_rights_created': 'Einladungsrechte angelegt',
  'legal_transaction.saved_with_warnings': 'mit Warnungen gespeichert',
  'process_manager.tracking_created': 'Nachverfolgung eingerichtet',
  'process_manager.tracking_state_changed': 'Nachverfolgung: Status geändert',
  'process_manager.tracking_removed': 'Nachverfolgung entfernt',
  'mail.sent': 'E-Mail versendet',
  'legal_transaction.exported': 'Vorgang exportiert',

  'record_number.created': 'Urkundennummer %s hinzugefügt',
  'record_number.deleted': 'Urkundennummer %s entfernt',
  'transaction_number.created': 'Vorgangsnummer %s hinzugefügt',
  'transaction_number.deleted': 'Vorgangsnummer %s entfernt',
  'custom_id.field_changed': 'Aktenzeichen des Mandanten geändert',
  'custom_id.created': 'Aktenzeichen vergeben',
  'custom_id.deleted': 'Aktenzeichen entfernt',
  'relationship.created': 'Beteiligtenstruktur angelegt',
  'relationship.deleted': 'Beteiligtenstruktur entfernt',
  'special_function.created': 'Sonderfunktion aktiviert',
  'special_function.deleted': 'Sonderfunktion entfernt',

  'portal.synced': 'ins Mandantenportal übertragen',
  'portal.sync_failed': 'Übertragung ins Mandantenportal fehlgeschlagen',
  'portal.sync_deleted': 'im Mandantenportal entfernt',

  'client.created': 'Mandant angelegt',
  'client.saved': 'Mandant gespeichert',
  'client.deleted': 'Mandant gelöscht',
  'client.merged': 'Mandant zusammengeführt',
  'client_account.blocked': 'Portal-Zugang gesperrt',
  'client_account.unblocked': 'Portal-Zugang entsperrt',
  'client_account.password_reset': 'Passwort zurückgesetzt',
  'client_account.password_reset_requested':
    'Zurücksetzen des Passworts angefragt',
  'client_data.submitted': 'Daten im Portal erfasst',

  'task.created': 'Aufgabe erstellt',
  'task.completed': 'Aufgabe erledigt',
  'task.reopened': 'Aufgabe wieder geöffnet',
  'task.deleted': 'Aufgabe gelöscht',
  'task.reminder_sent': 'Erinnerung versendet',

  'legal_phase.completed': 'Phase abgeschlossen',
  'legal_phase.reopened': 'Phase auf offen zurückgesetzt',
  'legal_requirement.created': 'Maßnahme angelegt',
  'legal_requirement.deleted': 'Maßnahme entfernt',
  'legal_requirement.state_changed': 'Maßnahmen-Status geändert',
  'deadline.triggered': 'Frist ausgelöst',

  'document_download.created': 'Dokument vom Mandanten heruntergeladen',
  'document_request.created': 'Dokument vom Mandanten angefragt',
  'document_upload.created': 'Dokument vom Mandanten hochgeladen',

  'invoice_payment.recorded': 'Zahlung erfasst',
  'invoice.created': 'Rechnung hinzugefügt',
  'invoice.deleted': 'Rechnung gelöscht',
  'document_directory.created': 'Datenordner erstellt',
  'document_directory.deleted': 'Datenordner gelöscht',
  'legal_role.created': 'Beteiligtenrolle erstellt',
  'legal_role.deleted': 'Beteiligtenrolle entfernt',
  'note.created': 'Notiz hinterlegt',
  'note.deleted': 'Notiz gelöscht',
  'bank_account.balance_changed': 'Kontostand geändert',
  'gwg_payment.created': 'Kaufpreiseinzahlung erfasst',

  'land_register.retrieved': 'Grundbuch abgerufen',
  'land_register.owner_changed': 'Eigentümer geändert',
  'land_register.changed': 'Grundbuchblatt geändert',
  'land_register.transaction_link_changed': 'Vorgangs-Verknüpfung geändert',
  'land_register.parcel_changed': 'Flurstück geändert',
  'land_register.logged': 'Eintrag am Grundbuchblatt',

  'meeting.created': 'Termin angelegt',
  'meeting_request.rejected': 'Terminanfrage abgewiesen',
  'meeting.rescheduled': 'Termin verschoben',
  'meeting.cancelled': 'Termin abgesagt',

  'relation.linked': 'Zuordnung hinzugefügt',
  'relation.unlinked': 'Zuordnung entfernt',

  // `%s` markiert die Stelle des Anzeigenamens. Ohne Platzhalter wird er
  // angehängt ("Vorgang geändert „Titel""); bei einer Datei liest sich die
  // aktive Form besser: „Dokument „Kaufvertrag.pdf" hochgeladen".
  'document.added_to_area': 'Dokument %s hochgeladen',
  'document.removed_from_area': 'Dokument %s gelöscht',
  'document.moved': 'Dokument %s verschoben',
  'document.renamed': 'Dokument %s umbenannt',

  'legacy.log_entry': 'Protokolleintrag',
};

/** Menschlicher Name für eine Entitätsart — für den Kontext-Chip. */
const ENTITY_LABEL: Record<string, string> = {
  LegalTransaction: 'Vorgang',
  LegalClient: 'Mandant',
  Task: 'Aufgabe',
  TaskComment: 'Kommentar',
  Document: 'Dokument',
  Invoice: 'Rechnung',
  InvoicePaid: 'Zahlung',
  LegalPhase: 'Phase',
  LegalRequirement: 'Maßnahme',
  LegalRequirement_deadlines: 'Frist',
  LegalRequirementState: 'Maßnahmen-Status',
  // Steht wörtlich so im Dialog: „Für diese Vorgangsmanager-Position sind noch
  // keine Attribute…" (CreateOrUpdateMeasureDialog, MeasuresList).
  Measurement: 'Vorgangsmanager-Position',
  MeasurementAttribute: 'Attribut',
  LegalRole: 'Beteiligtenrolle',
  LegalTransactionNote: 'Notiz',
  LegalTransactionMeeting: 'Termin',
  LegalTransactionRecordNumber: 'Urkundennummer',
  LegalTransactionTransactionNumber: 'Vorgangsnummer',
  LegalTransactionCustomId: 'Aktenzeichen',
  LegalTransactionGwGEntry: 'GwG-Zahlung',
  Grundbuchblatt: 'Grundbuchblatt',
  RealEstate: 'Immobilie',
  Relationship: 'Beteiligtenstruktur',
  Address: 'Adresse',
  ContactInformation: 'Kontaktdaten',
  Accounting: 'Bankverbindung',
  User: 'Benutzer',
  UserGroup: 'Benutzergruppe',
  Court: 'Gericht',
  Room: 'Raum',
  HbciAccount: 'Notaranderkonto',
  DocumentDownloaded: 'Download',
  DocumentRequested: 'Anfrage',
  DocumentUploaded: 'Upload',
  LegalTransactionLegalClient: 'Mandantenzuordnung',
  LegalRole_canInvite: 'Einladungsrecht',
  Precondition_LegalPhase: 'Phasen-Voraussetzung',
  LegalTransaction_has_Contact: 'Kontaktzuordnung',
  LegalTransaction_has_LegalTransactionSpecialFunction: 'Sonderfunktion',
  LegalClientUser: 'Portal-Zugang',
  LegalClientInvite: 'Portal-Einladung',
  LegalClientAddress: 'Adresszuordnung',
  LegalClientContactInformation: 'Kontaktzuordnung',
  LegalClientAccounting: 'Bankverbindungszuordnung',
  LegalClientDocument: 'Mandantendokument',
  LegalClientDocumentVersion: 'Dokumentversion',
  LegalClientGroup: 'Mandantengruppe',
  LegalClient_has_LegalClientGroup: 'Gruppenzuordnung',
  LegalClientGrundbuchblatt: 'Grundbuchzuordnung',
  TaskUser: 'Zuweisung',
  TaskUserGroup: 'Gruppenzuweisung',
  TaskCommentUser: 'Kommentar-Erwähnung',
  Grundstueck: 'Grundstück',
  DocumentDirectory: 'Datenordner',
  DocumentDirectory_has_Document: 'Ordnerzuordnung',
  DraftMessage: 'Entwurfsnachricht',
  IncomingEmail: 'Eingehende E-Mail',
  InvoiceLegalClient: 'Rechnungsempfänger',
  InvoicePaidPlan: 'Zahlungsplan',
  PurchaseContract: 'Kaufvertrag',
  RealEstatePurchaseContract: 'Kaufvertrag',
  RealEstatePurchaseContractTenant: 'Mieter',
  Mortgage: 'Grundschuld',
  Inherit: 'Erbfolge',
  CompanyFormation: 'Gesellschaftsgründung',
  LegalTransactionTalkRoom: 'Besprechungsraum',
  LegalTransactionMeetingEmail: 'Termin-E-Mail',
  LegalTransactionMeetingRequest: 'Terminanfrage',
  LegalTransactionContactInformation: 'Vorgangs-Kontaktdaten',
  LegalTransactionGwG: 'GwG-Prüfung',
  LegalClientRelationship: 'Mandantenbeziehung',
  LegalClientRole: 'Mandantenrolle',
  RatingResponse: 'Bewertungsantwort',
  RatingFormTriggerOnTransaction: 'Bewertungs-Auslöser',
  RealEstateLandRegisterEntry: 'Grundbucheintrag',
  Grundbuchblatt_has_RealEstate: 'Grundstückszuordnung',
  Relationship_has_Grundbuchblatt: 'Grundbuchverknüpfung',
  Automation: 'Automatisierung',
  LegalTransactionType: 'Rechtsgeschäftsart',
  LegalTransactionState: 'Vorgangsstatus',
  LegalTransactionProgress: 'Vollzugsstand',
  LegalClientType: 'Mandantenart',
  LegalPhaseTemplate: 'Phasenvorlage',
  LegalRequirementTemplate: 'Maßnahmenvorlage',
  LegalRoleTemplate: 'Rollenvorlage',
  MeetingEmailTemplate: 'Termin-E-Mail-Vorlage',
  DocumentDirectoryTemplate: 'Ordnervorlage',
  Setting: 'Einstellung',
  SystemMail: 'Systemnachricht',
  GlobalMail: 'Globale E-Mail',
  MailSetting: 'E-Mail-Einstellung',
  TextModule: 'Textbaustein',
  TextModuleCategory: 'Textbaustein-Kategorie',
  RelationshipType: 'Beziehungsart',
  RelationshipRole: 'Beziehungsrolle',
  TaskType: 'Aufgabentyp',
  TaskPriority: 'Priorität',
  Gender: 'Anrede',
  Country: 'Land',
  AcademicTitle: 'Titel',
  BusinessForm: 'Rechtsform',
  Postcode: 'Postleitzahl',
  Role: 'Berechtigungsrolle',
  MeetingType: 'Terminart',
  Location: 'Ort',
  GrundbuchblattOwner: 'Eigentümer',
  RealEstateLandParcel: 'Flurstück',
  LegalTransaction_has_Document: 'Dateizuordnung',
  LegalTransactionMeeting_has_LegalClient: 'Terminzuordnung',
  Task_has_Document: 'Aufgabenanhang',
};

export const entityLabel = (entityType: string): string =>
  ENTITY_LABEL[entityType] ?? entityType;

/** Bereich eines Ereignisses — der Chip vor der Zeile.
 *
 * Zuerst der Ablageort, wenn das Ereignis einen kennt ("Urkunden",
 * "Rechnungen"): das ist die genauere Auskunft als der Sammelbereich
 * "Dokumente". Sonst der Bereich, in dem die Entitätsart der geöffneten Akte
 * einsortiert ist.
 */
/**
 * Ein Bereich der Protokollansicht.
 *
 * Zugeordnet wird ueber die Entitaetsart — oder, wo die nichts hergibt, ueber
 * den Ereignistyp: Nachverfolgungen und Mails tragen als Entitaet die Akte
 * selbst, weil `ProcessManager` auf der Audit-Denylist steht und keine eigene
 * Entitaet liefern kann. Ohne diesen Weg laegen sie im Bereich „Vorgang" und
 * waeren dort nicht von einer Feldaenderung zu unterscheiden.
 */
export interface IAuditArea {
  label: string;
  entityTypes: string[];
  /** Praefixe von Ereignistypen, die ebenfalls in diesen Bereich gehoeren. */
  eventTypes?: string[];
}

export const areaLabel = (
  event: IAuditEvent,
  areas?: IAuditArea[]
): string | null => {
  const ablage = (event.Metadata ?? {})['area'];
  if (typeof ablage === 'string' && ablage) return ablage;
  if (!areas) return null;

  // Der Ereignistyp ist die genauere Aussage und gewinnt deshalb.
  const ueberEreignis = areas.find(area =>
    (area.eventTypes ?? []).some(prefix => event.EventType.startsWith(prefix))
  );
  if (ueberEreignis) return ueberEreignis.label;

  return (
    areas.find(area => area.entityTypes.includes(event.EntityType))?.label ??
    null
  );
};

/** Ein Bereich als Abfrageparameter fürs Backend.
 *
 * Gefiltert werden muss serverseitig: sonst durchsucht die Auswahl nur die
 * geladene Seite, und die Blätterleiste zeigt weiter die Seitenzahl des
 * ungefilterten Protokolls. Die Auflösung steht hier neben `areaLabel`, damit
 * Chip und Filter aus derselben Definition kommen — zwei getrennte Regeln
 * wären irgendwann auseinandergelaufen (#1330).
 *
 * Ein unbekannter Bereich wird als Name weitergereicht statt verworfen: er
 * trifft immer noch die Ereignisse, die ihn in ihren Metadaten tragen.
 */
export const areaQuery = (
  label: string,
  areas?: IAuditArea[]
): {
  areaLabel?: string;
  areaEntityTypes?: string;
  areaEventTypes?: string;
} => {
  if (!label) return {};

  const area = areas?.find(eintrag => eintrag.label === label);
  const query: {
    areaLabel?: string;
    areaEntityTypes?: string;
    areaEventTypes?: string;
  } = { areaLabel: label };

  if (area?.entityTypes?.length) {
    query.areaEntityTypes = area.entityTypes.join(',');
  }
  if (area?.eventTypes?.length) {
    query.areaEventTypes = area.eventTypes.join(',');
  }
  return query;
};

export const actorTypeLabel = (actorType: string): string =>
  ACTOR_LABEL[actorType] ?? actorType;

export const sourceLabel = (source: string): string =>
  SOURCE_LABEL[source] ?? source;

/** Akteur so, wie er in der Zeile steht: Name wenn vorhanden, sonst Rolle. */
export const actorText = (event: IAuditEvent): string => {
  const name = (event.ActorLabelSnapshot ?? '').trim();
  if (name) return name;
  return actorTypeLabel(event.ActorType);
};

/** Ein Wert für die Anzeige. Leer wird zum Gedankenstrich, damit man sieht,
 *  DASS etwas leer war. */
export const formatValue = (value: unknown, label?: string | null): string => {
  if (label) return label;
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'ja' : 'nein';
  if (typeof value === 'string') {
    // ISO-Datum bzw. -Zeitpunkt deutsch darstellen
    const match = value.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/
    );
    if (match) {
      const [, y, m, d, hh, mm] = match;
      return hh ? `${d}.${m}.${y}, ${hh}:${mm}` : `${d}.${m}.${y}`;
    }
    return value;
  }
  return String(value);
};

/** Satz für eine einzelne Feldänderung. */
export const changeText = (change: IAuditChange): string => {
  const feld = change.label || change.field;
  if (change.masked === 'secret') return `${feld} geändert`;
  if (change.masked === 'metadata') {
    const len = change.newMeta?.len;
    return len ? `${feld} geändert (${len} Zeichen)` : `${feld} geändert`;
  }
  const alt = formatValue(change.old, change.oldLabel);
  const neu = formatValue(change.new, change.newLabel);
  return `${feld}: „${alt}“ → „${neu}“`;
};

/** Ein Ende bzw. Merkmal einer Verknüpfung, wie das Backend es mitschreibt.
 *  Indiziert wird nach FELD, nicht nach Zieltabelle -- `LegalRole_canInvite`
 *  zeigt mit beiden Enden auf `LegalRole`. */
interface IRelationPartner {
  table: string;
  id: string;
  label?: string | null;
  /** true = Ende der Beziehung, false = Merkmal (Rolle, Bereich). */
  end?: boolean;
}

/** `Metadata.relation` einer Verknüpfungstabelle, typsicher gelesen.
 *
 * Eine Verknüpfungszeile hat selbst keinen Namen — ohne diese Angaben stünde im
 * Protokoll nur "Zuordnung entfernt". Das Backend legt die Namen beider Enden
 * als Snapshot dazu (#1330).
 */
const relationPartners = (event: IAuditEvent): IRelationPartner[] => {
  const roh = (event.Metadata ?? {})['relation'];
  if (!roh || typeof roh !== 'object') return [];
  // `table` fehlt bei Einträgen aus der ersten Fassung — dort war der Schlüssel
  // selbst die Zieltabelle. Ohne diesen Rückfall verlieren alte Einträge ihre
  // Zuordnung und würden als Beziehung zwischen zwei Fremden gelesen.
  return Object.entries(roh as Record<string, IRelationPartner>).map(
    ([key, wert]) => ({ ...wert, table: wert?.table ?? key })
  );
};

/** Die Enden einer Verknüpfung aus Sicht der geöffneten Akte: im Vorgang
 *  interessiert der Mandant, beim Mandanten der Vorgang. Bleiben zwei Enden
 *  übrig -- ein Einladungsrecht zwischen zwei Rollen etwa --, werden beide
 *  genannt. */
const enden = (event: IAuditEvent, viewType?: string): IRelationPartner[] =>
  relationPartners(event).filter(
    wert => wert.end !== false && wert.table !== viewType
  );

/** Merkmale einer Verknüpfung — die Beteiligtenrolle etwa. Nur, was auch einen
 *  Namen hat; eine nackte ID sagt dem Leser nichts. `ohne` unterdrückt eine
 *  Angabe, die dasselbe Wort wiederholt wie die Zeile selbst: "Mandant „X"
 *  zugeordnet (Mandant)" ist keine Zusatzinformation. */
const merkmale = (event: IAuditEvent, ohne?: string): string[] =>
  relationPartners(event)
    .filter(wert => wert.end === false && !!wert.label)
    .map(wert => String(wert.label))
    .filter(text => text !== ohne)
    .slice(0, 2);

/** Überschrift eines Ereignisses. */
export const eventText = (event: IAuditEvent): string => {
  const vorlage = EVENT_TEXT[event.EventType];
  if (vorlage) return vorlage;

  // Fallback, damit auch ein unbekannter Eventtyp lesbar bleibt: der Teil hinter
  // dem Punkt sagt, WAS passiert ist, der Teil davor WOMIT.
  const [, aktion] = event.EventType.split('.');
  const objekt = entityLabel(event.EntityType);
  const AKTION: Record<string, string> = {
    created: 'angelegt',
    field_changed: 'geändert',
    updated: 'geändert',
    deleted: 'gelöscht',
    bulk_deleted: 'mehrfach gelöscht',
    saved: 'gespeichert',
  };
  return `${objekt} ${AKTION[aktion ?? ''] ?? aktion ?? 'geändert'}`;
};

/** Vollständige Kopfzeile einer Ereigniszeile.
 *
 * Bewusst hier und nicht in der Komponente: Verknüpfungen, Dokumentbereiche und
 * der Anzeigename gehören in EINEN Satz, sonst entsteht "Zuordnung entfernt"
 * ohne Angabe, wessen Zuordnung. `viewType` ist die geöffnete Akte — aus ihrer
 * Sicht wird das Gegenstück benannt.
 */
export const eventHeadline = (
  event: IAuditEvent,
  viewType?: string
): string => {
  // Verknüpfung: die Gegenstücke benennen, nicht die Verknüpfungstabelle.
  const gegenstuecke = enden(event, viewType);
  const partner = gegenstuecke.length === 1 ? gegenstuecke[0] : undefined;
  const objekt = partner ? entityLabel(partner.table) : undefined;
  const zusatz = merkmale(event, objekt);
  const klammer = zusatz.length > 0 ? ` (${zusatz.join(', ')})` : '';

  const istVerknuepfung =
    event.EventType === 'relation.linked' ||
    event.EventType === 'relation.unlinked';

  if (partner && objekt) {
    const name = partner.label ? ` „${partner.label}“` : '';
    if (event.EventType === 'relation.linked') {
      return `${objekt}${name} zugeordnet${klammer}`;
    }
    if (event.EventType === 'relation.unlinked') {
      return `${objekt}${name} entfernt${klammer}`;
    }
    if (event.EventType.endsWith('.field_changed')) {
      return `Zuordnung ${objekt}${name} geändert${klammer}`;
    }
  }

  // Zwei Enden, keines davon die geöffnete Akte: eine Beziehung ZWISCHEN zwei
  // Dingen, etwa das Einladungsrecht von einer Beteiligtenrolle zur anderen.
  if (istVerknuepfung && gegenstuecke.length === 2) {
    const [von, nach] = gegenstuecke;
    const art = entityLabel(event.EntityType);
    const paar = `„${von.label ?? von.id}“ → „${nach.label ?? nach.id}“`;
    return event.EventType === 'relation.linked'
      ? `${art} ${paar} hinzugefügt${klammer}`
      : `${art} ${paar} entfernt${klammer}`;
  }

  /* Weder ein benennbares Gegenstueck noch zwei Enden. "Zuordnung
     hinzugefuegt" nannte dann nicht einmal, WAS zugeordnet wurde — mit der
     Entitaetsart steht wenigstens "Terminzuordnung hinzugefuegt" da. */
  if (istVerknuepfung) {
    const art = entityLabel(event.EntityType);
    return event.EventType === 'relation.linked'
      ? `${art} hinzugefügt${klammer}`
      : `${art} entfernt${klammer}`;
  }

  /* Artwechsel (#1330): beim Migrieren werden Phasen, Rollen und
     Einladungsrechte verworfen und aus der neuen Art neu aufgebaut. Ohne
     diesen Zusatz ist später nicht erklärbar, warum der Vollzugsplan anders
     aussieht. Ein unerwarteter Wert wird weggelassen statt angezeigt — die
     Angabe kommt aus den Metadaten und soll die Zeile nicht verunstalten. */
  if (event.EventType === 'legal_transaction.type_changed') {
    const vollzugsstand = (event.Metadata ?? {})['vollzugsstand'];
    const VOLLZUGSSTAND: Record<string, string> = {
      migriert: 'Vollzugsstand migriert',
      beibehalten: 'Vollzugsstand beibehalten',
    };
    const zusatzText =
      typeof vollzugsstand === 'string'
        ? VOLLZUGSSTAND[vollzugsstand]
        : undefined;
    const basis = eventText(event);
    return zusatzText ? `${basis} (${zusatzText})` : basis;
  }

  // Fertige Sätze aus einem Fachmodul, das sein eigenes Protokoll schon
  // formuliert hat (Grundbuch). Der Handelnde steht in der Gruppenzeile, der
  // Satz schließt daran an: „hat die Zuordnung zum Vorgang #4459 entfernt".
  const satz = (event.Metadata ?? {})['text'];
  if (typeof satz === 'string' && satz) {
    return satz;
  }

  /* Dokumente (#1330): ein Satz, der fuer sich steht. Der Bereich stand vorher
     nur im Chip daneben; im Protokoll gelesen sagte die knappe Fassung nicht,
     worum es geht: wohin. Die Wiederholung mit dem Chip ist bewusst in Kauf
     genommen - der Chip ist die Filterhilfe, der Satz die Aussage. */
  const ablageort = (event.Metadata ?? {})['area'];
  if (
    (event.EventType === 'document.added_to_area' ||
      event.EventType === 'document.removed_from_area') &&
    typeof ablageort === 'string' &&
    ablageort &&
    event.EntityLabelSnapshot
  ) {
    const datei = event.EntityLabelSnapshot;
    return event.EventType === 'document.added_to_area'
      ? `Das Dokument „${datei}“ wurde zu ${ablageort} hinzugefügt`
      : `Das Dokument „${datei}“ wurde aus ${ablageort} entfernt`;
  }

  /* Der Vollzugsplan aus der Rechtsgeschaeftsart: eine Zeile statt rund 24.
     Die einzelnen Phasen und Massnahmen stehen im Metadata und klappen in der
     Zeile hinter "mehr" auf. */
  const planPhasen = (event.Metadata ?? {})['phaseCount'];
  const planMassnahmen = (event.Metadata ?? {})['requirementCount'];
  if (
    event.EventType === 'legal_transaction.completion_plan_created' &&
    typeof planPhasen === 'number'
  ) {
    const art = (event.Metadata ?? {})['type'];
    const bezeichnung = typeof art === 'string' && art ? ` „${art}“` : '';
    const phasen = planPhasen === 1 ? '1 Phase' : `${planPhasen} Phasen`;
    const massnahmen =
      planMassnahmen === 1 ? '1 Maßnahme' : `${planMassnahmen} Maßnahmen`;
    return `Vollzugsplan${bezeichnung} angelegt: ${phasen}, ${massnahmen}`;
  }

  // Termine (#1330): „verschoben" allein sagt nicht, wohin. Die Zeiten stehen
  // im Metadata, weil das Datum am Termin selbst nur den neuen Stand kennt.
  const von = (event.Metadata ?? {})['from'];
  const nach = (event.Metadata ?? {})['to'];
  if (
    event.EventType === 'meeting.rescheduled' &&
    typeof von === 'string' &&
    typeof nach === 'string'
  ) {
    const name = event.EntityLabelSnapshot
      ? ` „${event.EntityLabelSnapshot}“`
      : '';
    return `Termin${name} vom ${formatValue(von)} auf ${formatValue(nach)} verschoben`;
  }

  const abgesagtAm = (event.Metadata ?? {})['at'];
  if (
    event.EventType === 'meeting.cancelled' &&
    typeof abgesagtAm === 'string'
  ) {
    const name = event.EntityLabelSnapshot
      ? ` „${event.EntityLabelSnapshot}“`
      : '';
    return `Termin${name} am ${formatValue(abgesagtAm)} abgesagt`;
  }

  // Nachverfolgungen (#1330, qs#24). Die Art steht im Metadata; ohne sie waere
  // jeder Eintrag nur „Nachverfolgung eingerichtet" und man muesste raten,
  // welche.
  const trackingType = (event.Metadata ?? {})['idProcessManagerType'];
  if (
    event.EventType.startsWith('process_manager.tracking_') &&
    typeof trackingType === 'number'
  ) {
    const art = TRACKING_TYPE_LABEL[trackingType] ?? 'Nachverfolgung';
    if (event.EventType === 'process_manager.tracking_created') {
      return `Nachverfolgung „${art}“ eingerichtet`;
    }
    if (event.EventType === 'process_manager.tracking_removed') {
      return `Nachverfolgung „${art}“ entfernt`;
    }
    const zustand = (event.Metadata ?? {})['idProcessManagerState'];
    const zustandName =
      typeof zustand === 'number' ? TRACKING_STATE_LABEL[zustand] : undefined;
    return zustandName
      ? `Nachverfolgung „${art}“: ${zustandName}`
      : `Nachverfolgung „${art}“ geändert`;
  }

  // Versendete Mail (#1330): der Betreff sagt, worum es ging — der Inhalt wird
  // bewusst nicht protokolliert.
  const subject = (event.Metadata ?? {})['subject'];
  if (
    event.EventType === 'mail.sent' &&
    typeof subject === 'string' &&
    subject
  ) {
    return `E-Mail „${subject}“ versendet`;
  }

  // Speichern trotz Health-Check-Warnungen (#1283). Die Liste der Funde steht
  // ebenfalls im Metadata und wird in der Zeile hinter „mehr" aufgeklappt —
  // hier nur die Zahl, sonst wäre die Überschrift ein Absatz.
  const warningCount = (event.Metadata ?? {})['warningCount'];
  if (
    event.EventType === 'legal_transaction.saved_with_warnings' &&
    typeof warningCount === 'number'
  ) {
    const warnungen =
      warningCount === 1 ? '1 Warnung' : `${warningCount} Warnungen`;
    const satz = `Vorgang mit ${warnungen} gespeichert`;
    /* Dasselbe Speichern, derselbe Satz: die Uebertragung haengt hinten dran,
       statt eine zweite Zeile zu bekommen. */
    const uebertragung = (event.Metadata ?? {})['syncState'];
    if (uebertragung === 'failed') {
      return `${satz}, aber NICHT ins Mandantenportal übertragen`;
    }
    if (uebertragung) return `${satz} und ins Mandantenportal übertragen`;
    return satz;
  }

  /* Speichern bzw. Anlegen und die Portal-Uebertragung zu einer Zeile
     zusammengefasst. Beim Anlegen gibt es kein Speichern-Ereignis; ohne diesen
     Zweig stand "ins Mandantenportal uebertragen" dort abgehackt als eigene
     Zeile. */
  const syncState = (event.Metadata ?? {})['syncState'];
  if (syncState && event.EventType.endsWith('.saved')) {
    return syncState === 'failed'
      ? 'Vorgang gespeichert, aber NICHT ins Mandantenportal übertragen'
      : 'Vorgang gespeichert und ins Mandantenportal übertragen';
  }
  if (syncState && event.EventType === 'legal_transaction.created') {
    const bezeichnung = event.EntityLabelSnapshot
      ? ` „${event.EntityLabelSnapshot}“`
      : '';
    return syncState === 'failed'
      ? `Vorgang angelegt${bezeichnung}, aber NICHT ins Mandantenportal übertragen`
      : `Vorgang angelegt${bezeichnung} und ins Mandantenportal übertragen`;
  }

  const name = event.EntityLabelSnapshot
    ? ` „${event.EntityLabelSnapshot}“`
    : '';
  const vorlage = EVENT_TEXT[event.EventType];

  // Der Ablageort ("Urkunden", "Rechnungen") steht im Bereichs-Chip vor der
  // Zeile — im Satz wäre er ein zweites Mal dasselbe.
  if (vorlage?.includes('%s')) {
    return `${vorlage.replace('%s', name.trim())}${klammer}`;
  }
  return `${eventText(event)}${name}${klammer}`;
};

/** Zeitgruppen-Überschrift: Heute / Gestern / Datum. */
export const dayHeading = (isoDate: string): string => {
  const datum = new Date(isoDate);
  if (Number.isNaN(datum.getTime())) return '';
  const heute = new Date();
  const gleicherTag = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (gleicherTag(datum, heute)) return 'Heute';
  const gestern = new Date(heute);
  gestern.setDate(gestern.getDate() - 1);
  if (gleicherTag(datum, gestern)) return 'Gestern';
  return datum.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const timeText = (isoDate: string): string => {
  const datum = new Date(isoDate);
  if (Number.isNaN(datum.getTime())) return '';
  return datum.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  });
};
